import { useEffect, useState, type FC } from "react";
import styled from "styled-components";
import { theme } from "../../theme";
import TitleSmall from "../atoms/typography/TitleSmall";
import { HorizontalSpacer } from "../atoms/Spacers";
import { Row } from "react-grid-system";
import BodySmall from "../atoms/typography/BodySmall";
import dayjs from "dayjs";
import Small from "../atoms/typography/Small";
import Body from "../atoms/typography/Body";
import { getMonitorEventTitles } from "../../utils/amlHelpers";
import { useTranslation } from "react-i18next";
import ListTabBar from "../molecules/ListTabBar";
import PaginationComponent from "../molecules/PaginationComponent";
import ClientEventDetailedActionComponent from "../molecules/aml/ClientEventDetailedActionComponent";
import LoadingSpinner from "../atoms/LoadingSpinner";
import Alert from "../molecules/Alert";
import CircleIcon from "../atoms/CircleIcon";
import Subheading from "../atoms/typography/Subheading";
import { useClientMonitoringEvents } from "../../graphql/hooks/aml/useClientMonitoringEvents";

interface Props {
  clientIdentifier: string;
}

const OutlinedDiv = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
  padding: 32px;
`;

const DetailedContainer = styled.div`
  width: 50%;
  padding: 16px;
  background-color: ${theme.colors.grey02};
`;

const InfoRuleBoxescontainer = styled.div`
  width: 50%;
`;

const InforRuleBox = styled.div<{ selected?: boolean }>`
  padding: 16px;
  background-color: ${(props) =>
    props.selected ? theme.colors.grey02 : "transparent"};
  border-bottom: 1px solid ${theme.colors.grey05};

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.hover};
  }
`;

const FlexedCenteredDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ITEMS_PER_PAGE = 10;
const tabs: Array<{ tabLabelKey: string; id: any }> = [
  {
    tabLabelKey: "aml.unhandled",
    id: "unhandled",
  },
  {
    tabLabelKey: "aml.handled",
    id: "handled",
  },
];

const ClientMonitoringEvents: FC<Props> = ({ clientIdentifier }) => {
  const { t } = useTranslation();
  const [selectedEvent, setSelectedEvent] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [currentTab, setCurrentTab] = useState("unhandled");
  const [errorMessage, setErrorMessage] = useState("");

  const { clientMonitoringEvents, error, loading, refetch } =
    useClientMonitoringEvents(
      clientIdentifier,
      currentTab === "unhandled" ? false : true,
      currentPage * ITEMS_PER_PAGE,
      ITEMS_PER_PAGE,
    );

  useEffect(() => {
    if (
      clientMonitoringEvents?.monitoringEvents?.items &&
      clientMonitoringEvents?.monitoringEvents?.items?.length > 0
    ) {
      setSelectedEvent(clientMonitoringEvents?.monitoringEvents?.items[0].id);
    }
  }, [clientMonitoringEvents]);

  const selectedEventDetails =
    clientMonitoringEvents?.monitoringEvents?.items.find(
      (event) => event.id === selectedEvent,
    );

  if (error) {
    setErrorMessage(
      t("aml.couldNotFetchMonitoringEvents") + ": " + error?.toString(),
    );
  }

  return (
    <div>
      <OutlinedDiv>
        <TitleSmall color={theme.colors.primary}>
          {t("aml.monitoringEvents")}
        </TitleSmall>
        <HorizontalSpacer spacing={2} />
        <ListTabBar
          tabs={tabs}
          selectedId={currentTab}
          onSelectTabId={(id) => setCurrentTab(id)}
        />
        {errorMessage && (
          <Alert
            type="error"
            title={t("errors.somethingWentWrong")}
            message={errorMessage}
          />
        )}
        {currentTab === "unhandled" && (
          <div>
            <HorizontalSpacer spacing={2} />
            <Subheading>{t("aml.needHandling")}</Subheading>
            <BodySmall color={theme.colors.textSecondary}>
              {t("aml.needHandlingDescription")}
            </BodySmall>
          </div>
        )}
        {loading && <LoadingSpinner />}
        <HorizontalSpacer spacing={1} />
        {error && (
          <Alert
            type="error"
            title={t("aml.couldNotFetchMonitoringEvents")}
            message={error.toString()}
          />
        )}

        <div style={{ display: "flex" }}>
          <InfoRuleBoxescontainer>
            {clientMonitoringEvents?.monitoringEvents?.items?.map((event) => (
              <InforRuleBox
                key={event.id}
                selected={selectedEvent === event.id}
                onClick={() => setSelectedEvent(event.id)}
              >
                <Row nogutter justify="between">
                  <FlexedCenteredDiv>
                    <CircleIcon type={event.handled ? "success" : "error"} />
                    <Body fontweight="500">
                      {t(getMonitorEventTitles(event.eventType))}
                    </Body>
                  </FlexedCenteredDiv>
                  {!event.handled && (
                    <Small color={theme.colors.error}>NY</Small>
                  )}
                  {event.handled && (
                    <BodySmall color={theme.colors.textSecondary}>
                      {t("aml.handledAt")}{" "}
                      {dayjs(event.handledAt).format("YYYY-MM-DD")}
                    </BodySmall>
                  )}
                </Row>
                <HorizontalSpacer spacing={0.5} />
                <FlexedCenteredDiv style={{ gap: 24 }}>
                  <BodySmall color={theme.colors.textSecondary}>
                    {t("aml.discovered")}{" "}
                    {dayjs(event.detectedAt).format("YYYY-MM-DD")}
                  </BodySmall>
                </FlexedCenteredDiv>
              </InforRuleBox>
            ))}
            <HorizontalSpacer spacing={1} />
            <PaginationComponent
              currentPageIndex={currentPage}
              numberOfPages={Math.ceil(
                (clientMonitoringEvents?.monitoringEvents?.totalCount || 0) /
                  ITEMS_PER_PAGE,
              )}
              setCurrentPageIndex={(newPage) => setCurrentPage(newPage)}
            />
          </InfoRuleBoxescontainer>
          <DetailedContainer>
            {selectedEventDetails && (
              <ClientEventDetailedActionComponent
                clientIdentifier={clientIdentifier}
                event={selectedEventDetails}
                refetch={refetch}
              />
            )}
          </DetailedContainer>
        </div>
      </OutlinedDiv>
    </div>
  );
};

export default ClientMonitoringEvents;
